import { useContext, useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  DevHubBox,
  DevHubCircularProgress,
  DevHubDynamicIcon,
  DevHubIconButton,
  DevHubTooltip,
} from '@devxdiscover/devhub-ui';
import { SystemContext } from '../../contexts/SystemContext';
import {
  ServiceBusiness,
  ServiceBusinessSelect,
} from '../common/ServiceBusinessSelect';
import { EditAppMutation } from './queries/InsightsQueries';
import AppInfoItem from '../common/AppInfoItem';

interface Props {
  applicationId: string;
  serviceBusinessItems: ServiceBusiness[];
  edit: boolean;
  hasEditPermission: boolean;
}

/**
 * Editable service business items field for the Insights About page.
 */
export const EditableServiceBusinessItems = ({
  applicationId,
  serviceBusinessItems,
  edit,
  hasEditPermission,
}: Props) => {
  const { GetApplicationQuery, applicationVariables } =
    useContext(SystemContext);
  const [editField, setEditField] = useState(false);
  const [selectedServiceBusinessItems, setSelectedServiceBusinessItems] =
    useState<ServiceBusiness[]>(serviceBusinessItems ?? []);

  const [editApp, { loading }] = useMutation(EditAppMutation, {
    refetchQueries: [
      {
        query: GetApplicationQuery,
        variables: applicationVariables,
      },
      'GetApplicationDetails',
    ],
  });

  const handleEdit = () => {
    setSelectedServiceBusinessItems(serviceBusinessItems ?? []);
    setEditField(prev => !prev);
  };

  const handleClose = () => {
    setEditField(false);
    setSelectedServiceBusinessItems(serviceBusinessItems ?? []);
  };

  const handleSubmit = () => {
    editApp({
      variables: {
        input: {
          applicationId: Number(applicationId),
          iopServiceBusinessItems: selectedServiceBusinessItems.map(
            sb => sb.number,
          ),
        },
      },
    });
    setEditField(false);
  };

  const displayText =
    serviceBusinessItems && serviceBusinessItems.length > 0
      ? serviceBusinessItems.map(sb => sb.name).join(', ')
      : 'None';

  return (
    <DevHubBox display="flex">
      {edit && editField ? (
        <DevHubBox flex={1}>
          <ServiceBusinessSelect
            multiple
            selectedServiceBusinessItems={selectedServiceBusinessItems}
            setSelectedServiceBusinessItems={setSelectedServiceBusinessItems}
            helperText="Select one or more service business items"
            required={false}
          />
        </DevHubBox>
      ) : (
        <AppInfoItem
          primaryText="IOP Service Business Items:"
          secondaryText={displayText}
        />
      )}
      {edit && !editField && (
        <>
          {loading ? (
            <DevHubCircularProgress thickness={6} size={22} />
          ) : (
            <DevHubTooltip title="Edit">
              <DevHubBox>
                <DevHubIconButton
                  size="medium"
                  disabled={!hasEditPermission}
                  onClick={handleEdit}
                >
                  <DevHubDynamicIcon iconName="Edit" />
                </DevHubIconButton>
              </DevHubBox>
            </DevHubTooltip>
          )}
        </>
      )}
      {edit && editField && (
        <DevHubBox display="inline-flex" ml={4} pt={2}>
          <DevHubTooltip title="Cancel">
            <DevHubBox>
              <DevHubIconButton onClick={handleClose}>
                <DevHubDynamicIcon iconName="CancelOutlined" />
              </DevHubIconButton>
            </DevHubBox>
          </DevHubTooltip>
          <DevHubTooltip title="Save">
            <DevHubBox>
              <DevHubIconButton onClick={handleSubmit}>
                <DevHubDynamicIcon iconName="Save" />
              </DevHubIconButton>
            </DevHubBox>
          </DevHubTooltip>
        </DevHubBox>
      )}
    </DevHubBox>
  );
};
